/**
 * The business name an email's domain implies — the signup form's pre-fill.
 *
 * Mirrors `domain_label` in `app/tenancy/workspace.py`, list for list: the
 * workspace address rule lets a business email claim only its own domain's
 * label (`info@augmentrait.com` → `augmentrait`, `x@acme.co.in` → `acme`), so
 * the form fills the address in from the email as it is typed. A free-mail
 * address has no business name to imply and pre-fills nothing; the server's
 * `GET /auth/workspace` and the signup itself are what actually decide.
 *
 * Both lists are copied verbatim from the Python rather than fetched, because
 * they are part of the rule and the rule has one home; a mismatch shows up as
 * a pre-fill the server then refuses with `WORKSPACE_MISMATCH`, never as a
 * signup the server accepts and the form would have refused.
 */

/** `FREE_MAIL_DOMAINS` in `app/tenancy/workspace.py`. */
const FREE_MAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.in',
  'yahoo.in',
  'ymail.com',
  'outlook.com',
  'hotmail.com',
  'hotmail.co.uk',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'aol.com',
  'zoho.com',
  'zohomail.in',
  'yandex.com',
  'yandex.ru',
  'rediffmail.com',
  'mail.com',
  'gmx.com',
  'gmx.de',
  'fastmail.com',
  'hey.com',
  'tutanota.com',
])

/** `MULTI_PART_SUFFIXES` in `app/tenancy/workspace.py`. */
const MULTI_PART_SUFFIXES: ReadonlySet<string> = new Set([
  'co.in',
  'net.in',
  'org.in',
  'ac.in',
  'gov.in',
  'co.uk',
  'org.uk',
  'ac.uk',
  'gov.uk',
  'com.au',
  'net.au',
  'org.au',
  'co.nz',
  'com.br',
  'com.sg',
  'com.my',
  'co.jp',
  'co.kr',
  'com.hk',
  'co.tw',
  'co.za',
  'com.mx',
  'com.ar',
])

/**
 * The label before the public suffix of `email`'s domain, or `null`.
 *
 * `null` for anything that is not yet an address with a two-label domain, and
 * for a free-mail domain — a personal inbox has no business name to imply.
 * Takes the whole email rather than the domain so the form can call it on
 * every keystroke without splitting first.
 */
export function domainLabel(email: string): string | null {
  const at = email.lastIndexOf('@')
  if (at < 0) return null
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .replace(/\.+$/, '')
  const labels = domain.split('.')
  if (labels.length < 2) return null
  const suffixLength =
    labels.length >= 3 && MULTI_PART_SUFFIXES.has(labels.slice(-2).join('.')) ? 2 : 1
  if (labels.length <= suffixLength) return null
  const registrable = labels.slice(-(suffixLength + 1)).join('.')
  if (FREE_MAIL_DOMAINS.has(registrable)) return null
  const label = labels[labels.length - (suffixLength + 1)] ?? ''
  return label === '' ? null : label
}
